// Assessment content (domain_*.json) in the reader's language.
//
// Each domain's English file in Storage (domains/domain_<name>.json) is the
// only source of structure: codes, SurveyMonkey question ids, colours. A
// translation is an overlay (domains/domain_<name>.<locale>.json) holding only
// the translated text, keyed by the same codes:
//
//     {
//       "name": "Mensaje",
//       "label": ["MUY LITERAL", ...],                 // by index
//       "feedback": { "a": "...", ... },
//       "attitudes": { "<code>": { "name": "...", "meaning": { "a": ... } } },
//       "level_two": { "<code>": { "name": "...", "behaviors": { "<code>": {...} } } }
//     }
//
// applyDomainOverlay() copies the English file and replaces only the text
// fields the overlay has, so anything untranslated -- including a behavior
// added in English after the translation was made -- shows in English rather
// than disappearing. `node scripts/i18n-check.js` reports what is missing.
import { fetchStorageJson } from './utility.js';

export const DOMAIN_NAMES = { m: 'message', r: 'relationship', t: 'time', s: 'space' };

// Plain text fields, and the fields that hold one text per score band (a-e).
export const TEXT_FIELDS = ['name', 'description', 'attitude_description', 'definition', 'quote', 'sample', 'left_label', 'right_label'];
export const BAND_FIELDS = ['feedback', 'meaning', 'strengths', 'recommendations'];

// The English original of a localized domain object. AI summaries are built
// from English -- their cache is shared by everyone in a group, whatever
// language each person reads in.
const ENGLISH = Symbol('english');
export const englishDomain = (domain) => (domain && domain[ENGLISH]) || domain;

const overlayText = (target, overlay) => {
    if (!overlay) return;
    for (const field of TEXT_FIELDS) {
        if (typeof overlay[field] === 'string' && overlay[field] && field in target) target[field] = overlay[field];
    }
    for (const field of BAND_FIELDS) {
        if (!target[field] || !overlay[field]) continue;
        target[field] = { ...target[field] };
        for (const band of Object.keys(target[field])) {
            if (typeof overlay[field][band] === 'string' && overlay[field][band]) target[field][band] = overlay[field][band];
        }
    }
};

const overlayList = (list, overlays, extra) => (list || []).map((item) => {
    const copy = { ...item };
    const overlay = overlays && overlays[item.code];
    overlayText(copy, overlay);
    if (extra) extra(copy, overlay);
    return copy;
});

export const applyDomainOverlay = (base, overlay) => {
    if (!base) return base;
    const out = { ...base };
    if (overlay) {
        overlayText(out, overlay);
        if (Array.isArray(base.label)) {
            out.label = base.label.map((text, i) => (overlay.label && overlay.label[i]) || text);
        }
        out.attitudes = overlayList(base.attitudes, overlay.attitudes);
        out.level_two = overlayList(base.level_two, overlay.level_two, (copy, l2) => {
            copy.behaviors = overlayList(copy.behaviors, l2 && l2.behaviors);
        });
    }
    Object.defineProperty(out, ENGLISH, { value: englishDomain(base), enumerable: false });
    return out;
};

export const domainOverlayPath = (name, locale) => `domains/domain_${name}.${locale}.json`;

// One fetch per domain and language per page load. A missing overlay resolves
// to null, which leaves the domain in English.
const overlayCache = new Map();
export const fetchDomainOverlay = (name, locale) => {
    if (!locale || locale === 'en') return Promise.resolve(null);
    const path = domainOverlayPath(name, locale);
    if (!overlayCache.has(path)) overlayCache.set(path, fetchStorageJson(path));
    return overlayCache.get(path);
};

// The reverse of applyDomainOverlay, for the domain editor: given a domain
// edited in a translation, keep only the text that is actually translated.
// A field is kept if it differs from English, or if the previous overlay
// already had it (a translation can legitimately equal the English, e.g.
// "Message" in French). Everything else is left out so it keeps falling back
// to English -- and keeps showing up as untranslated in i18n-check.
export const extractDomainOverlay = (edited, english, previous) => {
    const pickText = (e, en, prev) => {
        const out = {};
        if (!e) return out;
        for (const field of TEXT_FIELDS) {
            const value = e[field];
            if (typeof value !== 'string' || !value) continue;
            if (value !== (en && en[field]) || (prev && prev[field] !== undefined)) out[field] = value;
        }
        for (const field of BAND_FIELDS) {
            if (!e[field]) continue;
            const bands = {};
            for (const [band, value] of Object.entries(e[field])) {
                if (typeof value !== 'string' || !value) continue;
                const was = prev && prev[field] ? prev[field][band] : undefined;
                if (value !== (en && en[field] && en[field][band]) || was !== undefined) bands[band] = value;
            }
            if (Object.keys(bands).length) out[field] = bands;
        }
        return out;
    };
    const byCode = (list, enList, prevMap, extra) => {
        const out = {};
        for (const item of list || []) {
            const en = (enList || []).find((x) => x.code === item.code);
            const prev = prevMap && prevMap[item.code];
            const picked = pickText(item, en, prev);
            if (extra) extra(picked, item, en, prev);
            if (Object.keys(picked).length) out[item.code] = picked;
        }
        return out;
    };

    const overlay = pickText(edited, english, previous);
    if (Array.isArray(edited.label)) {
        const changed = edited.label.some((text, i) => text !== (english.label || [])[i]);
        if (changed || (previous && previous.label)) overlay.label = [...edited.label];
    }
    const attitudes = byCode(edited.attitudes, english.attitudes, previous && previous.attitudes);
    if (Object.keys(attitudes).length) overlay.attitudes = attitudes;
    const levelTwo = byCode(edited.level_two, english.level_two, previous && previous.level_two, (picked, item, en, prev) => {
        const behaviors = byCode(item.behaviors, en && en.behaviors, prev && prev.behaviors);
        if (Object.keys(behaviors).length) picked.behaviors = behaviors;
    });
    if (Object.keys(levelTwo).length) overlay.level_two = levelTwo;
    return overlay;
};
